// Header bell + recent-notifications panel. Live-updates via
// /api/sse/notifications by pushing into the cache (no list re-fetch).
// Renders a popover at sm+ and a bottom sheet at <sm; same body in both.
//
// The same stream is also the source for OS-level notifications, which is why
// the opt-in toggle lives in this panel rather than in profile settings: a
// reader turns desktop banners on where they just read the thing they wish
// they had been told about. See lib/desktopNotifications.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Ban,
  Bell,
  BellOff,
  BellRing,
  Check,
  CheckCheck,
  Eye,
  EyeOff,
  Info,
  Loader2,
} from 'lucide-react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Sheet } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import { api, type Notification } from '@/lib/api';
import { cn } from '@/lib/classnames';
import {
  type DesktopNotifyStatus,
  sendTestNotification,
  showDesktopNotification,
  useDesktopNotifications,
  useNotifiedOnce,
} from '@/lib/desktopNotifications';
import { formatRelativeTime } from '@/lib/format';
import { isInAppPath } from '@/lib/links';
import { useSSE } from '@/lib/sse';
import { useIsMobile } from '@/lib/useMediaQuery';

type NotifPage = { notifications: Notification[]; unread: number };
const QKEY = ['notifications'] as const;

// Optimistic read-state marker. read_at is only ever read for truthiness
// (read vs unread) in this component, never displayed or parsed as a date, so
// we use a neutral non-clock sentinel rather than the client clock (which can
// skew from the server's timestamp); the next refetch replaces it with the
// authoritative server value (FE-CORR-07).
const READ_MARKER = 'read';

export function NotificationsBell() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const desktop = useDesktopNotifications();
  const firstSighting = useNotifiedOnce();

  const q = useQuery({
    queryKey: QKEY,
    queryFn: () => api.listNotifications({ limit: 30 }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  useSSE(
    user ? '/api/sse/notifications' : null,
    (ev) => {
      const n = notificationFromSSE(ev.data);
      if (!n) return;
      qc.setQueryData<NotifPage>(QKEY, (prev) => {
        if (!prev) return { notifications: [n], unread: 1 };
        if (prev.notifications.some((x) => x.id === n.id)) return prev;
        return {
          notifications: [n, ...prev.notifications].slice(0, 30),
          unread: prev.unread + 1,
        };
      });
      // Every row the feed gets, the OS gets - a run that FAILED is as much
      // news to someone who walked away as one that finished, and filtering
      // here would leave the banner and the panel disagreeing about what
      // happened. showDesktopNotification is a no-op unless the reader opted
      // in and this tab is hidden.
      if (!firstSighting(n.id)) return;
      const link = typeof n.link === 'string' && isInAppPath(n.link) ? n.link : null;
      showDesktopNotification({
        tag: n.id,
        title: n.title,
        body: n.body,
        onClick: link ? () => void navigate(link) : undefined,
      });
    },
    ['notification'],
  );

  useEffect(() => {
    if (!open || isMobile) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, isMobile]);

  // Single toggle for both the per-row eye button and link-activation. The
  // mark endpoints return {status:ok}, not the row, so we patch read_at from
  // the requested state + adjust the unread count by the actual delta.
  const setRead = useMutation({
    mutationFn: ({ id, read }: { id: string; read: boolean }) =>
      read ? api.markNotificationRead(id) : api.markNotificationUnread(id),
    onSuccess: (_res, { id, read }) => {
      qc.setQueryData<NotifPage>(QKEY, (prev) => {
        if (!prev) return prev;
        let delta = 0;
        const notifications = prev.notifications.map((n) => {
          if (n.id !== id) return n;
          const wasUnread = !n.read_at;
          if (read && wasUnread) delta = -1;
          else if (!read && !wasUnread) delta = 1;
          return { ...n, read_at: read ? READ_MARKER : null };
        });
        return { notifications, unread: Math.max(0, prev.unread + delta) };
      });
    },
  });

  const markAll = useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: () => {
      qc.setQueryData<NotifPage>(QKEY, (prev) =>
        prev
          ? {
              notifications: prev.notifications.map((n) =>
                n.read_at ? n : { ...n, read_at: READ_MARKER },
              ),
              unread: 0,
            }
          : prev,
      );
    },
  });

  const data = q.data;
  const unread = data?.unread ?? 0;
  const items = data?.notifications ?? [];

  const onActivate = useCallback((n: Notification) => {
    if (!n.read_at) setRead.mutate({ id: n.id, read: true });
    setOpen(false);
  }, [setRead]);

  const onToggleRead = useCallback(
    (n: Notification) => setRead.mutate({ id: n.id, read: !n.read_at }),
    [setRead],
  );

  const panel = (
    <NotificationsList
      items={items}
      isLoading={q.isLoading}
      onActivate={onActivate}
      onToggleRead={onToggleRead}
    />
  );

  const markAllBtn = (
    <button
      onClick={() => markAll.mutate()}
      disabled={unread === 0 || markAll.isPending}
      className="ring-focus text-xs text-ink-400 hover:text-ink-100 inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed h-9 px-2 rounded-md"
    >
      {markAll.isPending ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <CheckCheck className="w-3.5 h-3.5" />
      )}
      Mark all read
    </button>
  );

  const desktopBtn = (
    <DesktopAlertsToggle
      status={desktop.status}
      onEnable={desktop.enable}
      onDisable={desktop.disable}
    />
  );

  const header = (
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-ink-800/60">
      <div className="text-sm font-medium text-ink-100">Notifications</div>
      <div className="flex items-center gap-1">
        {desktopBtn}
        {markAllBtn}
      </div>
    </div>
  );

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'ring-focus relative inline-flex items-center justify-center w-10 h-10 rounded-md hover:bg-ink-900 transition-colors',
          // Unread → bright bell (the at-a-glance indicator); read → muted.
          unread > 0 ? 'text-ink-50' : 'text-ink-400 hover:text-ink-100',
        )}
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
        aria-haspopup={isMobile ? 'dialog' : 'menu'}
        aria-expanded={open}
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-accent-500 ring-2 ring-ink-950" />
        )}
      </button>

      {/* Phone: bottom sheet. */}
      {isMobile ? (
        <Sheet
          open={open}
          onOpenChange={setOpen}
          title="Notifications"
          contentClassName="max-h-[80dvh]"
        >
          <div className="flex items-center justify-end gap-1 px-4 py-2 border-b border-ink-800/60">
            {desktopBtn}
            {markAllBtn}
          </div>
          {panel}
        </Sheet>
      ) : (
        // ≥sm: popover. Width clamps to the viewport so it never clips
        // off-screen on narrow tablet portraits.
        open && (
          <div className="absolute right-0 mt-1.5 w-[min(24rem,calc(100vw-1.5rem))] card p-0 z-50 animate-slide-down overflow-hidden">
            {header}
            <div className="max-h-[26rem] overflow-y-auto overscroll-y-auto">{panel}</div>
          </div>
        )
      )}
    </div>
  );
}

// The opt-in control for OS-level banners, plus the one way to prove they
// arrive.
//
// Nothing is rendered on a browser that cannot show them (or on a plain-HTTP
// deployment, where the permission is denied before it is asked): a control
// that cannot do anything is worse than its absence. A permission the reader
// already refused is shown, disabled, saying where it can be undone - our
// toggle genuinely cannot reverse that, and a button that silently fails
// would read as a broken feature.
//
// A banner fires once, automatically, the moment the grant lands. That is the
// confirmation: the feature is invisible by construction - a real one only
// ever appears on a tab nobody is watching - and without it a reader cannot
// tell an alert that works from one that will never come. There is no standing
// test control beside the toggle; after that first banner there is nothing
// left for one to answer.
function DesktopAlertsToggle({
  status,
  onEnable,
  onDisable,
}: {
  status: DesktopNotifyStatus;
  onEnable: () => Promise<DesktopNotifyStatus>;
  onDisable: () => void;
}) {
  const toast = useToast();

  // Granted and then refused: the browser took the permission and the OS still
  // showed nothing. Silence here would read as "the test button is broken",
  // which is the opposite of what this control is for.
  const announce = useCallback(() => {
    if (!sendTestNotification()) {
      toast.error('Your browser accepted the permission but would not show the notification.');
    }
  }, [toast]);

  const toggle = useCallback(async () => {
    if (status === 'on') {
      onDisable();
      return;
    }
    if ((await onEnable()) === 'on') announce();
  }, [status, onEnable, onDisable, announce]);

  if (status === 'unsupported') return null;

  const on = status === 'on';
  const blocked = status === 'blocked';
  const Icon = on ? BellRing : BellOff;

  return (
    <button
      type="button"
      // The grant prompt only opens from a user gesture, which is this click.
      onClick={() => void toggle()}
      disabled={blocked}
      aria-pressed={on}
      title={
        blocked
          ? 'This browser is blocking notifications for this site. Allow them in site settings.'
          : on
            ? 'Desktop alerts on. Raised while this tab is in the background.'
            : 'Get a desktop alert when a run finishes while this tab is in the background.'
      }
      className={cn(
        'ring-focus text-xs inline-flex items-center gap-1.5 h-9 px-2 rounded-md disabled:opacity-40 disabled:cursor-not-allowed',
        on ? 'text-accent-300 hover:text-accent-200' : 'text-ink-400 hover:text-ink-100',
      )}
    >
      <Icon className="w-3.5 h-3.5" />
      Desktop alerts
    </button>
  );
}

function NotificationsList({
  items,
  isLoading,
  onActivate,
  onToggleRead,
}: {
  items: Notification[];
  isLoading: boolean;
  onActivate: (n: Notification) => void;
  onToggleRead: (n: Notification) => void;
}) {
  if (isLoading) {
    return (
      <div className="px-4 py-6 text-xs text-ink-500 text-center">
        <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />
        Loading…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="px-4 py-10 text-center text-xs text-ink-500">
        <Bell className="w-5 h-5 text-ink-700 mx-auto mb-2" />
        No notifications.
      </div>
    );
  }
  return (
    <div className="divide-y divide-ink-800/60">
      {items.map((n) => (
        <NotificationRow
          key={n.id}
          n={n}
          onActivate={() => onActivate(n)}
          onToggleRead={() => onToggleRead(n)}
        />
      ))}
    </div>
  );
}

const NotificationRow = memo(function NotificationRow({
  n,
  onActivate,
  onToggleRead,
}: {
  n: Notification;
  onActivate: () => void;
  onToggleRead: () => void;
}) {
  const { Icon, tone } = visualFor(n.type);
  const unread = !n.read_at;
  // Icon + text. The activatable area wraps this; the read/unread eye toggle
  // sits beside it as a sibling (never nested inside the Link/button).
  const inner = (
    <>
      <Icon className={cn('w-4 h-4 mt-0.5 flex-shrink-0', tone)} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <div className={cn('text-sm truncate', unread ? 'text-ink-100 font-medium' : 'text-ink-300')}>
            {n.title}
          </div>
          {unread && <span className="w-1.5 h-1.5 rounded-full bg-accent-500 shrink-0" />}
        </div>
        {n.body && (
          <div className="text-xs text-ink-400 mt-0.5 line-clamp-2">{n.body}</div>
        )}
        <div className="text-[11px] text-ink-500 mt-1">{formatRelativeTime(n.created_at)}</div>
      </div>
    </>
  );

  const mainClass = 'flex items-start gap-3 flex-1 min-w-0 px-4 py-3 text-left';
  // A link, only if it names a route in this app. react-router writes an
  // absolute `to` into the anchor verbatim with its SPA handler removed, so an
  // off-origin or `javascript:` value here would be a one-click navigation off
  // the dashboard. The notifier only ever writes `/submissions/<id>`; this is
  // what keeps a wire string from becoming a target if that ever changes.
  // Checked at the sink rather than on arrival because a row reaches this from
  // two sources - the SSE push and the REST list - and only one of them has a
  // parser of its own.
  const target = typeof n.link === 'string' && isInAppPath(n.link) ? n.link : null;
  const main = target ? (
    <Link to={target} onClick={onActivate} className={mainClass}>
      {inner}
    </Link>
  ) : (
    <button type="button" onClick={onActivate} className={cn(mainClass, 'bg-transparent')}>
      {inner}
    </button>
  );

  return (
    <div className="group flex items-stretch hover:bg-ink-900/60 transition-colors">
      {main}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggleRead();
        }}
        title={unread ? 'Mark as read' : 'Mark as unread'}
        aria-label={unread ? 'Mark as read' : 'Mark as unread'}
        className="ring-focus shrink-0 self-center mr-2 w-8 h-8 rounded-md flex items-center justify-center text-ink-500 hover:text-ink-100 hover:bg-ink-800 transition-colors"
      >
        {unread ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      </button>
    </div>
  );
});

// Rebuilds a Notification row from the SSE envelope's data payload. The
// notifier publishes id/type/title/body/link/created_at; a push is always a
// fresh (unread) insert. Rows missing required fields are dropped rather than
// rendered half-empty.
function notificationFromSSE(data: Record<string, unknown> | undefined): Notification | null {
  if (!data) return null;
  const { id, type, title, created_at, body, link } = data;
  if (
    typeof id !== 'string' ||
    typeof type !== 'string' ||
    typeof title !== 'string' ||
    typeof created_at !== 'string'
  ) {
    return null;
  }
  return {
    id,
    type,
    title,
    created_at,
    body: typeof body === 'string' ? body : null,
    link: typeof link === 'string' ? link : null,
    read_at: null,
  };
}

function visualFor(type: string): { Icon: typeof Bell; tone: string } {
  switch (type) {
    case 'submission.completed':
      return { Icon: Check, tone: 'text-verdict-clean' };
    case 'submission.failed':
      return { Icon: AlertCircle, tone: 'text-verdict-malicious' };
    case 'submission.canceled':
      return { Icon: Ban, tone: 'text-ink-400' };
    default:
      return { Icon: Info, tone: 'text-accent-300' };
  }
}
